import * as amplitude from "@amplitude/analytics-browser";
import { createContext, useContext } from "react";

import { AMPLITUDE_API_KEY, IS_PROD } from "./constants";
import { Company } from "./pages/home/useCompany";
import { Employee } from "./pages/home/useEmployee";

const ACTIONS = {
  CLICK: "click",
  NAVIGATION: "navigation",
  SHOWED: "showed",
};

interface ActionType {
  screenName: string;
  additionalProperties?: { [key: string]: unknown };
}

interface ClickActionType extends ActionType {
  componentName: string;
}
interface NavigationActionType extends ActionType {
  to: string;
}

interface ShowedActionType extends ActionType {
  componentName: string;
}

const Metrics = {
  init: () => {
    if (!IS_PROD) return;

    amplitude.init(AMPLITUDE_API_KEY, {
      defaultTracking: false,
      cookieOptions: {
        secure: true,
      },
    });
  },
  identify: (employee: Employee, companyInfo: Company) => {
    const identifyEvent = new amplitude.Identify();

    const data = {
      user_id: employee.id,
      userEmail: employee.email,
      company: companyInfo?.name,
      companyId: employee.companyId,
      state: employee.state,
      email: employee.email,
      first_name: employee.name,
      last_name: employee.surname,
      country: companyInfo.country,
      home_city: employee.homeAddress?.locality,
      gender: employee.sex,
      dob: employee.birthDate,
      phone: employee.mobilePhone,
      companySource: companyInfo.source,
      contractType: companyInfo.contractType,
    };

    Object.entries(data).map(([key, value]) => {
      identifyEvent.set(`${key}`, `${value}`);
    });
    amplitude.setUserId(employee.id);
    amplitude.identify(identifyEvent);
  },
  trackAction: {
    click: ({
      screenName,
      componentName,
      additionalProperties,
    }: ClickActionType) =>
      amplitude.track(screenName, {
        action: ACTIONS.CLICK,
        componentName,
        ...additionalProperties,
      }),
    navigation: ({
      screenName,
      to,
      additionalProperties,
    }: NavigationActionType) =>
      amplitude.track(screenName, {
        action: ACTIONS.NAVIGATION,
        to,
        ...additionalProperties,
      }),
    showed: ({
      screenName,
      componentName,
      additionalProperties,
    }: ShowedActionType) =>
      amplitude.track(screenName, {
        action: ACTIONS.SHOWED,
        componentName,
        ...additionalProperties,
      }),
  },
  trackEvent: (
    eventName: string,
    additionalProperties?: { [key: string]: unknown }
  ) => {
    amplitude.track(eventName, additionalProperties);
  },
  pageView: (eventName: string) => amplitude.track("Viewed " + eventName),
};

const MetricsContext = createContext(Metrics);

const useMetrics = () => useContext(MetricsContext);

export { MetricsContext, Metrics, useMetrics };
